// Translate: one page for typed and spoken translation (replaces the old
// home menu, Voice Translation and Text Translation pages).
//
// Typed text goes through the live session when the backend supports
// `text_message` (same translation + TTS path as speech) and falls back to the
// REST /translate endpoint otherwise. Speech uses the session's audio stream.

import { icons, bars } from '../shared/icons.js';
import { languageName, languageOptions, isSupportedLanguage, langTag } from '../shared/languages.js';
import { escapeHtml, toast, bindMenu, copyText, formatTime } from '../shared/dom.js';
import { createComposer } from '../shared/composer.js';
import { createMicController, micErrorMessage } from '../shared/mic.js';
import { createReadAloudControl, isReadAloudOn } from '../shared/read-aloud.js';
import { playTranslation, stopPlayback, speakIfNoStream } from '../shared/playback.js';
import { primeEarcons } from '../shared/earcons.js';

const HISTORY_KEY = 'polyglot-history-v2';
const LEGACY_HISTORY_KEY = 'polyglot-translation-history';
const HISTORY_LIMIT = 40;
const SOURCE_KEY = 'polyglot-translate-source';
const TARGET_KEY = 'polyglot-translate-target';

let initialized = false;

function inittranslate() {
    if (initialized) return;
    initialized = true;
    const page = document.getElementById('page-translate');
    buildPage(page);
}

window.inittranslate = inittranslate;

// ---------------------------------------------------------------------------

function loadHistory() {
    try {
        const saved = JSON.parse(localStorage.getItem(HISTORY_KEY) || 'null');
        // Saved entries are always finished ones; saveHistory() leaves the status out,
        // and without it the next save would drop them all.
        if (Array.isArray(saved)) return saved.map((e) => ({ ...e, status: 'done' }));
    } catch {
        // fall through to migration
    }
    // One-time migration from the old text-translation history.
    try {
        const legacy = JSON.parse(localStorage.getItem(LEGACY_HISTORY_KEY) || '[]');
        return legacy.reverse().map((item, i) => ({
            id: `legacy-${i}-${Date.parse(item.timestamp) || i}`,
            mode: 'typed',
            source: item.original,
            translation: item.translated,
            sourceLang: item.sourceLang,
            targetLang: item.targetLang,
            status: 'done',
            ts: Date.parse(item.timestamp) || Date.now(),
        }));
    } catch {
        return [];
    }
}

function saveHistory(entries) {
    const done = entries.filter((e) => e.status === 'done').slice(-HISTORY_LIMIT);
    const slim = done.map(({ id, mode, source, translation, sourceLang, targetLang, ts, messageId }) =>
        ({ id, mode, source, translation, sourceLang, targetLang, ts, messageId }));
    try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(slim));
    } catch {
        // Storage full or blocked: history just won't persist.
    }
}

function defaultLanguages() {
    let source = localStorage.getItem(SOURCE_KEY);
    let target = localStorage.getItem(TARGET_KEY);
    if (!isSupportedLanguage(source)) {
        const preferred = localStorage.getItem('polyglot-user-language');
        source = isSupportedLanguage(preferred) ? preferred : 'eng';
    }
    if (!isSupportedLanguage(target) || target === source) {
        target = source === 'swa' ? 'eng' : 'swa';
    }
    return { source, target };
}

// ---------------------------------------------------------------------------

function buildPage(page) {
    const websocketService = window.websocketService;
    const audioService = window.audioService;
    const apiService = window.apiService;
    const ttsService = window.ttsAudioService;

    const langs = defaultLanguages();
    const state = {
        source: langs.source,
        target: langs.target,
        entries: loadHistory(),
        // Ids of entries the user removed while still translating, so late results don't bring them back.
        removed: new Set(),
        selectedId: null,
        playingId: null,
        sessionId: null,
        participantId: null,
        connecting: null,      // Promise while (re)connecting
        connected: false,
        textMessagesSupported: true,
    };
    const autoPlayQueue = new Set();
    let clientSeq = 0;

    ttsService.setAutoPlay(isReadAloudOn());

    page.innerHTML = `
        <header class="pg-header tr-header">
            <a class="pg-brand" href="/" aria-label="Polyglot home">
                <span class="pg-brand__mark">${icons.globe}</span>
                <span class="pg-brand__name">Polyglot</span>
            </a>
            <span class="tr-header__readaloud"></span>
            <span class="pg-menu-anchor">
                <button type="button" class="pg-iconbtn" id="tr-menu-btn" aria-label="Menu">${icons.dots}</button>
                <div class="pg-menu" id="tr-menu" hidden>
                    <a class="pg-menu__item" href="/room/">${icons.users}<span>Rooms</span></a>
                    <button type="button" class="pg-menu__item" data-action="settings">${icons.settings}<span>Settings</span></button>
                    <button type="button" class="pg-menu__item" data-action="install">${icons.install}<span>Install app</span></button>
                    <div class="pg-menu__sep" role="separator"></div>
                    <button type="button" class="pg-menu__item pg-menu__item--danger" data-action="clear">${icons.trash}<span>Clear history</span></button>
                </div>
            </span>
        </header>

        <div class="tr-langbar" role="group" aria-label="Languages">
            <label class="tr-lang">
                <span class="tr-lang__label">From</span>
                <select class="tr-lang__select" id="tr-source">${languageOptions(state.source)}</select>
            </label>
            <button type="button" class="tr-swap" id="tr-swap" aria-label="Swap languages">${icons.swap}</button>
            <label class="tr-lang">
                <span class="tr-lang__label">To</span>
                <select class="tr-lang__select" id="tr-target">${languageOptions(state.target)}</select>
            </label>
        </div>

        <div class="tr-body">
            <section class="tr-history" aria-label="Translations">
                <div class="pg-banner tr-banner" id="tr-banner" hidden>
                    <div class="pg-banner__body" id="tr-banner-text"></div>
                    <button type="button" id="tr-banner-action" hidden>Retry</button>
                </div>
                <div class="tr-history__head" hidden>
                    <h2 class="pg-eyebrow tr-history__title">History</h2>
                    <button type="button" class="tr-reset" id="tr-reset">${icons.trash}<span>Start over</span></button>
                </div>
                <div class="tr-empty" id="tr-empty">
                    <h1 class="pg-display tr-empty__title">What do you want to say?</h1>
                    <ul class="tr-empty__list">
                        <li><span class="tr-empty__icon">${icons.pencil}</span>Type in the box below</li>
                        <li><span class="tr-empty__icon">${icons.mic}</span><span id="tr-empty-speak">Or tap the mic and speak</span></li>
                        <li><span class="tr-empty__icon tr-empty__icon--accent">${icons.speaker}</span><span id="tr-empty-aloud"></span></li>
                    </ul>
                </div>
                <ol class="tr-feed" id="tr-feed"></ol>
            </section>

            <div class="tr-stage">
                <section class="tr-current" id="tr-current" aria-live="polite" aria-label="Current translation"></section>
                <div class="tr-composer-slot" id="tr-composer-slot"></div>
            </div>
        </div>
    `;

    const sourceSelect = page.querySelector('#tr-source');
    const targetSelect = page.querySelector('#tr-target');
    const feed = page.querySelector('#tr-feed');
    const empty = page.querySelector('#tr-empty');
    const currentPane = page.querySelector('#tr-current');
    const banner = page.querySelector('#tr-banner');
    const bannerText = page.querySelector('#tr-banner-text');
    const bannerAction = page.querySelector('#tr-banner-action');

    page.querySelector('.tr-header__readaloud').replaceWith(createReadAloudControl({ variant: 'pill' }));

    // Menu
    bindMenu(page.querySelector('#tr-menu-btn'), page.querySelector('#tr-menu'));
    page.querySelector('#tr-menu').addEventListener('click', (e) => {
        const action = e.target.closest('[data-action]')?.dataset.action;
        if (action === 'settings') window.showPage('page-settings');
        if (action === 'install') window.showPage('page-install');
        if (action === 'clear') startOver();
    });
    page.querySelector('#tr-reset').addEventListener('click', startOver);

    // Clears the whole list (and what's saved on this device). Undo puts it back.
    function startOver() {
        if (!state.entries.length) return;
        stopPlayback();
        setPlaying(null);
        const saved = { entries: state.entries, selectedId: state.selectedId };
        state.entries.forEach(forget);
        state.entries = [];
        state.selectedId = null;
        saveHistory(state.entries);
        renderAll();
        toast('History cleared', 6000, {
            action: {
                label: 'Undo',
                run: () => {
                    state.entries = [...saved.entries, ...state.entries];
                    state.selectedId = saved.selectedId;
                    saveHistory(state.entries);
                    renderAll();
                },
            },
        });
    }

    function forget(entry) {
        if (entry.status !== 'pending') return;
        [entry.id, entry.messageId, entry.clientId].filter(Boolean).forEach((id) => state.removed.add(id));
    }

    // Removes one translation. Undo puts it back where it was.
    function removeEntry(entry) {
        const index = state.entries.indexOf(entry);
        if (index < 0) return;
        if (state.playingId === entry.id) {
            stopPlayback();
            setPlaying(null);
        }
        forget(entry);
        state.entries.splice(index, 1);
        if (state.selectedId === entry.id) {
            const next = state.entries[Math.min(index, state.entries.length - 1)];
            state.selectedId = next ? next.id : null;
        }
        saveHistory(state.entries);
        renderAll({ scroll: false });
        toast('Removed', 6000, {
            action: {
                label: 'Undo',
                run: () => {
                    state.entries.splice(Math.min(index, state.entries.length), 0, entry);
                    saveHistory(state.entries);
                    renderAll({ scroll: false });
                },
            },
        });
    }

    // Composer
    const composer = createComposer({
        placeholder: 'Type, or tap the mic',
        sendLabel: 'Translate',
        onSubmit: (text) => submitText(text),
        onMicPress: () => toggleMic(),
    });
    page.querySelector('#tr-composer-slot').replaceWith(composer.el);

    const mic = createMicController({
        audioService,
        websocketService,
        ttsService,
        speakingThreshold: 0.02,
        onLevel: (v) => composer.setLevel(v),
        onPause: () => {},
        onChange: (on) => {
            composer.setListening(on);
            if (!on) composer.setStatus('');
        },
    });

    // ------------------------------------------------------------------
    // Rendering

    function entryById(id) {
        return state.entries.find((e) => e.id === id);
    }

    function entryByMessageId(messageId) {
        return state.entries.find((e) => e.messageId === messageId);
    }

    function entryHtml(entry) {
        const modeIcon = entry.mode === 'spoken' ? icons.mic : icons.pencil;
        const modeLabel = entry.mode === 'spoken' ? 'Spoken' : 'Typed';
        const playing = state.playingId === entry.id;
        let status = '';
        let translation = '';
        if (entry.status === 'pending') {
            status = '<span class="tr-entry__status"><span class="pg-spinner" aria-hidden="true"></span>Translating…</span>';
            translation = '';
        } else if (entry.status === 'error') {
            status = '<span class="tr-entry__status tr-entry__status--error">Couldn\'t translate this. Try again.</span>';
        } else {
            translation = escapeHtml(entry.translation);
            if (playing) status = `<span class="tr-entry__status tr-entry__status--playing">${bars}Reading aloud</span>`;
        }
        const remove = `<button type="button" class="pg-iconbtn tr-remove" data-action="remove" aria-label="Remove from history" title="Remove">${icons.x}</button>`;
        const actions = `
            <div class="tr-entry__actions">
                ${entry.status === 'done' ? `
                <button type="button" class="pg-iconbtn tr-play${playing ? ' is-playing' : ''}" data-action="play" aria-label="${playing ? 'Stop' : 'Play translation'}">${playing ? icons.stop : icons.speaker}</button>
                <button type="button" class="pg-iconbtn" data-action="copy" aria-label="Copy translation">${icons.copy}</button>` : ''}
                ${remove}
            </div>`;
        return `
            <li class="tr-entry${state.selectedId === entry.id ? ' is-selected' : ''}${playing ? ' is-playing' : ''}" data-id="${escapeHtml(entry.id)}">
                <button type="button" class="tr-entry__open" data-action="open">
                    <span class="tr-entry__meta"><span class="tr-entry__mode" role="img" aria-label="${modeLabel}">${modeIcon}</span><span class="tr-entry__time">${modeLabel} · ${formatTime(entry.ts)}</span></span>
                    <span class="tr-entry__src">${escapeHtml(entry.source)}</span>
                </button>
                ${translation ? `<p class="tr-entry__tr" lang="${langTag(entry.targetLang)}">${translation}</p>` : ''}
                <div class="tr-entry__foot">${status}${actions}</div>
            </li>`;
    }

    function renderEntry(entry) {
        const existing = feed.querySelector(`[data-id="${CSS.escape(entry.id)}"]`);
        const html = entryHtml(entry);
        if (existing) {
            existing.outerHTML = html;
        } else {
            feed.insertAdjacentHTML('beforeend', html);
        }
        if (state.selectedId === entry.id) renderCurrent();
        updateEmpty();
    }

    function renderCurrent() {
        const entry = entryById(state.selectedId);
        if (!entry) {
            currentPane.innerHTML = `
                <span class="pg-eyebrow">${escapeHtml(languageName(state.target))}</span>
                <p class="tr-current__placeholder">Your translation will appear here.</p>`;
            return;
        }
        const playing = state.playingId === entry.id;
        let body;
        if (entry.status === 'pending') {
            body = '<p class="tr-current__placeholder"><span class="pg-spinner" aria-hidden="true"></span> Translating…</p>';
        } else if (entry.status === 'error') {
            body = '<p class="tr-current__placeholder">Couldn\'t translate this. Try again.</p>';
        } else {
            body = `<p class="tr-current__text pg-display" lang="${langTag(entry.targetLang)}">${escapeHtml(entry.translation)}</p>`;
        }
        currentPane.innerHTML = `
            <span class="pg-eyebrow">${escapeHtml(languageName(entry.targetLang))}</span>
            ${body}
            <div class="tr-current__foot" data-id="${escapeHtml(entry.id)}">
                <span class="tr-current__source">${escapeHtml(entry.source)}</span>
                ${entry.status === 'done' ? `
                <span class="tr-current__status">${playing ? `${bars}Reading aloud` : ''}</span>
                <div class="tr-entry__actions">
                    <button type="button" class="pg-iconbtn tr-play${playing ? ' is-playing' : ''}" data-action="play" aria-label="${playing ? 'Stop' : 'Play translation'}">${playing ? icons.stop : icons.speaker}</button>
                    <button type="button" class="pg-iconbtn" data-action="copy" aria-label="Copy translation">${icons.copy}</button>
                    <button type="button" class="pg-iconbtn tr-remove" data-action="remove" aria-label="Remove from history" title="Remove">${icons.x}</button>
                </div>` : `
                <div class="tr-entry__actions">
                    <button type="button" class="pg-iconbtn tr-remove" data-action="remove" aria-label="Remove from history" title="Remove">${icons.x}</button>
                </div>`}
            </div>`;
    }

    function updateEmpty() {
        const hasEntries = state.entries.length > 0;
        empty.hidden = hasEntries;
        page.querySelector('.tr-history__head').hidden = !hasEntries;
        page.querySelector('#tr-empty-speak').textContent = `Or tap the mic and speak ${languageName(state.source)}`;
        page.querySelector('#tr-empty-aloud').textContent = isReadAloudOn()
            ? `${languageName(state.target)} translations will be read aloud`
            : 'Read aloud is off. Tap the speaker on any card to hear it.';
    }

    function renderAll({ scroll = true } = {}) {
        feed.innerHTML = state.entries.map(entryHtml).join('');
        if (!entryById(state.selectedId)) {
            state.selectedId = state.entries.length ? state.entries[state.entries.length - 1].id : null;
        }
        renderCurrent();
        updateEmpty();
        if (scroll) scrollFeedToEnd();
    }

    function scrollFeedToEnd() {
        const scroller = page.querySelector('.tr-history');
        requestAnimationFrame(() => {
            scroller.scrollTop = scroller.scrollHeight;
        });
    }

    function select(id) {
        const prev = state.selectedId;
        state.selectedId = id;
        if (prev && prev !== id) {
            const prevEntry = entryById(prev);
            if (prevEntry) renderEntry(prevEntry);
        }
        const entry = entryById(id);
        if (entry) renderEntry(entry);
        renderCurrent();
    }

    function addEntry(entry) {
        state.entries.push(entry);
        if (state.entries.length > HISTORY_LIMIT + 10) {
            const removed = state.entries.splice(0, state.entries.length - HISTORY_LIMIT);
            removed.forEach((e) => feed.querySelector(`[data-id="${CSS.escape(e.id)}"]`)?.remove());
        }
        state.selectedId = entry.id;
        // Re-render the previously selected card so it loses its highlight.
        feed.querySelectorAll('.tr-entry.is-selected').forEach((el) => el.classList.remove('is-selected'));
        renderEntry(entry);
        scrollFeedToEnd();
    }

    function updateEntry(entry, changes) {
        Object.assign(entry, changes);
        renderEntry(entry);
        if (entry.status === 'done') saveHistory(state.entries);
    }

    function setPlaying(id) {
        const prev = state.playingId;
        state.playingId = id;
        [prev, id].forEach((x) => {
            const e = x && entryById(x);
            if (e) renderEntry(e);
        });
        if (!id || !prev) renderCurrent();
    }

    // Card actions (event delegation for both the feed and the current pane)
    async function onCardAction(e) {
        const button = e.target.closest('[data-action]');
        if (!button) return;
        const holder = button.closest('[data-id]');
        const entry = holder && entryById(holder.dataset.id);
        if (!entry) return;
        const action = button.dataset.action;
        if (action === 'open') {
            select(entry.id);
        } else if (action === 'remove') {
            removeEntry(entry);
        } else if (action === 'copy') {
            if (await copyText(entry.translation)) toast('Translation copied');
        } else if (action === 'play') {
            if (state.playingId === entry.id) {
                stopPlayback();
                setPlaying(null);
                return;
            }
            play(entry);
        }
    }
    feed.addEventListener('click', onCardAction);
    currentPane.addEventListener('click', onCardAction);

    async function play(entry) {
        try {
            await playTranslation({
                messageId: entry.messageId || entry.id,
                lang: entry.targetLang,
                text: entry.translation,
                onStart: () => setPlaying(entry.id),
                onEnd: () => { if (state.playingId === entry.id) setPlaying(null); },
            });
        } catch (error) {
            console.warn('Playback failed', error);
            toast('Audio isn\'t available for this one');
        }
    }

    // ------------------------------------------------------------------
    // Banner

    function showBanner(text, { error = false, action = null } = {}) {
        bannerText.textContent = text;
        banner.classList.toggle('pg-banner--error', error);
        banner.hidden = false;
        if (action) {
            bannerAction.textContent = action.label;
            bannerAction.hidden = false;
            bannerAction.onclick = action.run;
        } else {
            bannerAction.hidden = true;
            bannerAction.onclick = null;
        }
    }

    function hideBanner() {
        banner.hidden = true;
    }

    // ------------------------------------------------------------------
    // Connection to the live session (needed for speech, preferred for text)

    function bindSocketHandlers() {
        const socket = websocketService.getSocket();
        if (!socket) return;
        socket.off('message_complete', onMessageComplete);
        socket.off('translation_update', onTranslationUpdate);
        socket.off('transcription_progress', onTranscriptionProgress);
        socket.off('tts_audio_chunk', onTtsChunk);
        socket.off('disconnect', onDisconnect);
        socket.off('connect', onReconnect);
        socket.on('message_complete', onMessageComplete);
        socket.on('translation_update', onTranslationUpdate);
        socket.on('transcription_progress', onTranscriptionProgress);
        socket.on('tts_audio_chunk', onTtsChunk);
        socket.on('disconnect', onDisconnect);
        socket.on('connect', onReconnect);
    }

    async function connect() {
        if (state.connected) return;
        if (state.connecting) return state.connecting;
        composer.setMicEnabled(false, 'Connecting…');
        state.connecting = (async () => {
            try {
                if (!websocketService.isConnected()) {
                    await websocketService.connect();
                }
                if (!state.sessionId) {
                    const session = await apiService.createSession(
                        `Translate ${new Date().toISOString()}`,
                        'Translate',
                        [state.source, state.target],
                        { skip_source_tts: true, lean: true }
                    );
                    state.sessionId = session.id;
                }
                const participant = await websocketService.joinSession(
                    state.sessionId,
                    localStorage.getItem('polyglot-user-name') || 'Speaker',
                    state.source
                );
                state.participantId = participant.id;
                bindSocketHandlers();
                state.connected = true;
                composer.setMicEnabled(true);
                hideBanner();
            } catch (error) {
                console.error('Translate: connection failed', error);
                state.connected = false;
                // Session may have expired server-side; make a new one next time.
                state.sessionId = null;
                composer.setMicEnabled(false, 'Not connected');
                showBanner('Can\'t reach the translation service right now. Typed text will still try to translate.', {
                    error: true,
                    action: { label: 'Retry', run: () => connect() },
                });
                throw error;
            } finally {
                state.connecting = null;
            }
        })();
        return state.connecting;
    }

    function onDisconnect() {
        state.connected = false;
        if (mic.isListening) mic.stop({ cue: true });
        composer.setMicEnabled(false, 'Reconnecting…');
    }

    function onReconnect() {
        if (state.connected) return;
        // socket.io reconnected the transport; rejoin the session.
        connect().catch(() => {});
    }

    // ------------------------------------------------------------------
    // Server events

    function onMessageComplete(data) {
        if (!data || data.sessionId && data.sessionId !== state.sessionId) return;
        if (state.removed.has(data.messageId) || state.removed.has(data.clientId)) return;
        let entry = data.clientId ? state.entries.find((e) => e.clientId === data.clientId) : null;
        if (!entry) entry = entryByMessageId(data.messageId);
        if (entry) {
            updateEntry(entry, { messageId: data.messageId, source: data.text || entry.source });
            return;
        }
        if (!data.text) return;
        if (composer.isListening) composer.setTranscript('');
        addEntry({
            id: data.messageId,
            messageId: data.messageId,
            mode: data.isTyped ? 'typed' : 'spoken',
            source: data.text,
            sourceLang: data.language || state.source,
            targetLang: state.target,
            translation: null,
            status: 'pending',
            ts: Date.now(),
        });
    }

    function onTranslationUpdate(data) {
        if (!data) return;
        const entry = entryByMessageId(data.messageId);
        if (!entry || data.targetLanguage !== entry.targetLang) return;
        updateEntry(entry, { translation: data.translatedText, status: 'done' });
        if (isReadAloudOn()) autoPlayQueue.add(data.messageId);
    }

    function onTranscriptionProgress(data) {
        if (composer.isListening && data?.text) {
            composer.setTranscript(data.text);
        }
    }

    function onTtsChunk(data) {
        const entry = entryByMessageId(data.messageId);
        if (!entry || data.languageCode !== entry.targetLang) return;
        ttsService.handleTTSAudioChunk(data);
        if (data.isLast && autoPlayQueue.has(data.messageId) && isReadAloudOn()) {
            const chunkType = data.isPartialAudio ? 'partial' : 'final';
            const part = Number.isInteger(data.audioPartIndex) ? data.audioPartIndex : 0;
            const key = `${data.messageId}_${data.languageCode}_${chunkType}_p${part}`;
            ttsService.queueAudioForPlayback(key, {
                onStart: () => setPlaying(entry.id),
                onEnd: () => { if (state.playingId === entry.id) setPlaying(null); },
            });
            if (!data.isPartialAudio) autoPlayQueue.delete(data.messageId);
        }
    }

    // ------------------------------------------------------------------
    // Typed text

    async function submitText(text) {
        primeEarcons();
        const clientId = `c${Date.now().toString(36)}${(++clientSeq).toString(36)}`;
        const entry = {
            id: clientId,
            clientId,
            messageId: null,
            mode: 'typed',
            source: text,
            sourceLang: state.source,
            targetLang: state.target,
            translation: null,
            status: 'pending',
            ts: Date.now(),
        };
        addEntry(entry);

        if (state.textMessagesSupported) {
            try {
                if (!state.connected) await connect();
                const ack = await websocketService.sendTextMessage(text, clientId);
                if (!entry.messageId) updateEntry(entry, { messageId: ack.messageId });
                return;
            } catch (error) {
                if (error?.message === 'ack_timeout') {
                    // Older backend without text_message: stop trying the socket path.
                    state.textMessagesSupported = false;
                }
                console.info('Translate: typed text falling back to REST', error?.message);
            }
        }

        try {
            const result = await apiService.translateText(text, entry.sourceLang, entry.targetLang);
            const translation = result.translated_text || result.translation;
            if (!translation) throw new Error('Empty translation');
            updateEntry(entry, { translation, status: 'done' });
            if (isReadAloudOn()) {
                speakIfNoStream({
                    messageId: entry.id,
                    lang: entry.targetLang,
                    text: translation,
                    waitMs: 0,
                    onStart: () => setPlaying(entry.id),
                    onEnd: () => { if (state.playingId === entry.id) setPlaying(null); },
                });
            }
        } catch (error) {
            console.error('Translate: text translation failed', error);
            updateEntry(entry, { status: 'error' });
        }
    }

    // ------------------------------------------------------------------
    // Speech

    async function toggleMic() {
        primeEarcons();
        if (mic.isListening) {
            mic.stop();
            return;
        }
        try {
            if (!state.connected) await connect();
            stopPlayback();
            composer.setListenLabel(`Listening · ${languageName(state.source)}`);
            await mic.start();
        } catch (error) {
            console.warn('Translate: mic start failed', error);
            showBanner(micErrorMessage(error), {
                error: true,
                action: error?.code === 'not_connected'
                    ? { label: 'Retry', run: () => connect().catch(() => {}) }
                    : null,
            });
        }
    }

    // ------------------------------------------------------------------
    // Languages

    function applyLanguages() {
        localStorage.setItem(SOURCE_KEY, state.source);
        localStorage.setItem(TARGET_KEY, state.target);
        sourceSelect.value = state.source;
        targetSelect.value = state.target;
        composer.setMicLabel(`Speak ${languageName(state.source)}`);
        composer.setPlaceholder(`Type in ${languageName(state.source)}, or tap the mic`);
        updateEmpty();
        if (!state.selectedId) renderCurrent();
        if (state.connected) {
            websocketService.updateParticipantLanguage(state.source);
            websocketService.updateSessionLanguages([state.source, state.target]);
        }
    }

    function setLanguages(source, target) {
        if (mic.isListening) mic.stop();
        if (source === target) return;
        state.source = source;
        state.target = target;
        applyLanguages();
    }

    // Picking the language already on the other side swaps the two.
    sourceSelect.addEventListener('change', () => {
        const value = sourceSelect.value;
        setLanguages(value, value === state.target ? state.source : state.target);
    });
    targetSelect.addEventListener('change', () => {
        const value = targetSelect.value;
        setLanguages(value === state.source ? state.target : state.source, value);
    });
    page.querySelector('#tr-swap').addEventListener('click', () => setLanguages(state.target, state.source));

    window.addEventListener('pg:readaloud', () => updateEmpty());

    // Stop the mic if the user navigates to another page (settings, install).
    window.addEventListener('pg:pagechange', (e) => {
        if (e.detail?.pageId !== 'page-translate' && mic.isListening) mic.stop();
    });

    // ------------------------------------------------------------------

    applyLanguages();
    renderAll();
    connect().catch(() => {});
}

export { inittranslate };
